import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type {
  EventRefundRequestDto,
  DenyEventRefundRequestDto,
  ResponseDto,
} from "@mimber/schema"
import { eventRefundsKeys } from "../events.utils"

export type DenyRefundRequestVariables = {
  refundUuid: string
  data: DenyEventRefundRequestDto
}

export const useDenyRefundRequest = (
  eventUuid: string,
  options?: UseMutationOptions<
    ResponseDto<EventRefundRequestDto>,
    Error,
    DenyRefundRequestVariables
  >
): UseMutationResult<
  ResponseDto<EventRefundRequestDto>,
  Error,
  DenyRefundRequestVariables
> => {
  guardAppUsage("mimber-organization", "useDenyRefundRequest")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async ({ refundUuid, data }: DenyRefundRequestVariables) => {
      const response = await server.post<ResponseDto<EventRefundRequestDto>>(
        `events/${eventUuid}/refunds/${refundUuid}/deny`,
        data
      )
      return response.data
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: eventRefundsKeys.findAll(eventUuid),
      })
      queryClient.invalidateQueries({
        queryKey: eventRefundsKeys.findOne(eventUuid, variables.refundUuid),
      })
      options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}
